import { createSlice } from '@reduxjs/toolkit';
import type { RootState } from '../../app/store';
import { IProject } from '../../types/project';

const getCurrentDateString = () => new Date(Date.now());

const initialState: IProjectState = {
  projects: [],
  project: {
    _id: '',
    name: '',
    developerName: '',
    addresses: [],
    imageGetUrl: null,
    imagePutUrl: '',
    logoGetUrl: null,
    logoPutUrl: '',
    developerLogoGetUrl: null,
    developerLogoPutUrl: '',
    partnerLogoGetUrl: null,
    partnerLogoPutUrl: '',
    email: '',
    docusignEmail: '',
    trackingPhone: '',
    salesTrackingPhone: '',
    options: [],
    depositStructures: [
      {
        name: '',
        deposits: [
          {
            name: '', // 1st Deposit, 2nd Deposit etc
            type: '', // Fixed or Percent
            amount: 0, // 5,000 or 2.5%
            daysDue: 0, // 30 days
            dateType: '',
            dueDate: getCurrentDateString(), // Specific Date
          },
        ],
        default: true,
      },
    ],
    mergeTemplates: [
      {
        name: 'floorplan',
        apsTemplates: [
          {
            name: 'Main APS',
            pageNumber: 0,
            attachToAps: '',
          },
        ],
      },
    ],
    executors: [],
    coopStructures: [],
    adjustments: [],
    commissionIncludeOptions: false,
    status: [],
    // emailTemplates: [],
    mortgageLimit: 2,
    hideOccupancy: true,
    defaultZeroValue: '$0',
    mortgageMinDay: 30,
    lawyer: '',
    tagLine: '',
    salesOffice: '',
    decorModels: [],
    combinedProjects: [],
    lease: null,
    firstTentativeOccupancy: null,
    finalTentativeOccupancy: null,
    firmOccupancy: null,
    outsideOccupancy: null,
    active: false,
    acknowledgement: false,
    executionDeposit: false,
    automatedReminders: false,
    postRegistration: false,
    netHst: false,
    leasing: false,
    leaseCc: [],
    tags: [],
    letter: '',
    supplementals: [],
  },
};

export const projectSlice = createSlice({
  name: 'project',
  initialState,
  reducers: {
    setProjects: (state, action) => {
      state.projects = action.payload;
    },
    // DashboardOutlet's light project query and CreateProject's full one both dispatch here for the
    // same project id (DashboardOutlet wraps the settings route), and can resolve in either order.
    // A raw replace would let the light payload (which omits settings-only fields like
    // combinedProjects) clobber full data, or - on the very first dispatch for a project - drop those
    // fields to undefined instead of the initialState default ([]), crashing any .length/.map read
    // that assumes the field exists. Merging onto state (same project) or initialState.project (new
    // project) guarantees every field the light query doesn't fetch still has a safe default.
    setProject: (state, action) => {
      if (state.project._id && state.project._id === action.payload._id) {
        state.project = { ...state.project, ...action.payload };
      } else {
        state.project = { ...initialState.project, ...action.payload };
      }
    },
    setMergeTemplate: (state, action) => {
      state.project.mergeTemplates = action.payload;
    },
    setOptions: (state, action) => {
      state.project.options = action.payload;
    },
    setDepositStructures: (state, action) => {
      state.project.depositStructures = action.payload;
    },
    setSupplementals: (state, action) => {
      state.project.supplementals = action.payload;
    },
    reset: () => initialState,
  },
});

export const { setProjects, setProject, setMergeTemplate, setOptions, setDepositStructures, reset } = projectSlice.actions;

export const selectProjects = (state: RootState) => state.project.projects;
export const selectProject = (state: RootState) => state.project.project;

interface IProjectState {
  projects: IProject[];
  project: IProject;
}

export default projectSlice.reducer;
